import { Language } from '@contracts/client/Language';
import type { QuickbooksMapping } from '@contracts/client/QuickbooksMapping';
import { DeliveryTypeOption as DeliveryTypeOptionContract } from '@contracts/order/valueNormalizers/DeliveryTypeOption';
import type { BillingCurrency } from '@contracts/payment/Currency';
import type { PaymentMethod } from '@contracts/payment/PaymentMethod';
import type { ClientForSelector } from '@src/clients/ClientSelectOption';
import type { Agency } from '@src/clientUser/agency/Agency';
import isEqual from 'lodash.isequal';

export { DeliveryTypeOptionContract as DeliveryTypeOption };

export interface Contact {
  phone: string;
  email: string;
  name: string;
}

export type ClientDeliveryTypeOption = Exclude<
  DeliveryTypeOptionContract,
  DeliveryTypeOptionContract.SMS
>;

export interface DeliveryType {
  deliveryType: ClientDeliveryTypeOption;
  value?: string;
}

export interface PermitNotifications {
  email?: string;
  phone?: string;
}

export interface Address {
  address: string;
  addressDetails?: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface InsuranceInformation {
  insuranceCompany?: string;
  insuranceAddressNumber?: string;
  insuranceEffectiveDate?: string;
  insuranceExperienceDate?: string;
  insurancePolicyNumber?: string;
  insuranceCoverageAmount?: string;
  insuranceAgentAmount?: string;
  insurancePhoneNumber?: string;
  insuranceIsOnFileWithState?: boolean;
}

interface JurisdictionIdNumber {
  field: string;
  value: string;
}

export interface ClientAttributes {
  name: string;
  doingBusinessAs?: string;
  isAgency?: boolean;
  agencyId?: string;
  communicationLanguage: Language;
  id: string;
  fax?: string;
  primaryContact: Contact;
  address: Address;
  mailingAddress: Address;
  primaryDeliveryType: DeliveryType;
  secondaryDeliveryType?: DeliveryType;
  permitNotifications?: PermitNotifications;
  paymentMethod?: PaymentMethod;
  billingCurrency?: BillingCurrency;
  requirePO: boolean;
  invoicingInstructions?: string;
  defaultBillToClientId?: string;
  insuranceInformation?: InsuranceInformation;
  jurisdictionIdNumbers: JurisdictionIdNumber[];
  isAgencyClient?: boolean;
  isArchived: boolean;
  quickbooksMapping?: QuickbooksMapping;
}

export class Client {
  public name: string;
  public doingBusinessAs?: string;
  public communicationLanguage: Language = Language.ENGLISH;
  public id: string;
  public fax?: string;
  public primaryContact: Contact;
  public address: Address;
  public mailingAddress: Address;
  public primaryDeliveryType: DeliveryType;
  public secondaryDeliveryType?: DeliveryType;
  public permitNotifications?: PermitNotifications;
  public paymentMethod?: PaymentMethod;
  public currency?: BillingCurrency;
  public requirePO: boolean;
  public invoicingInstructions?: string;
  public defaultBillToClientId?: string;
  public insuranceInformation?: InsuranceInformation;
  public jurisdictionIdNumbers: JurisdictionIdNumber[];
  public isAgencyClient?: boolean;
  public _isArchived: boolean;
  public quickbooksMapping?: QuickbooksMapping;
  protected _isAgency?: boolean;

  constructor(attribute: ClientAttributes) {
    this.id = attribute.id;
    this.name = attribute.name;
    this.doingBusinessAs = attribute.doingBusinessAs;
    this._isAgency = attribute.isAgency;
    this.communicationLanguage = attribute.communicationLanguage;
    this.fax = attribute.fax;
    this.primaryContact = attribute.primaryContact;
    this.address = attribute.address;
    this.mailingAddress = attribute.mailingAddress;
    this.primaryDeliveryType = attribute.primaryDeliveryType;
    this.secondaryDeliveryType = attribute.secondaryDeliveryType;
    this.permitNotifications = attribute.permitNotifications;
    this.paymentMethod = attribute.paymentMethod;
    this.currency = attribute.billingCurrency;
    this.requirePO = attribute.requirePO;
    this.invoicingInstructions = attribute.invoicingInstructions;
    this.defaultBillToClientId = attribute.defaultBillToClientId;
    this.insuranceInformation = attribute.insuranceInformation;
    this.jurisdictionIdNumbers = attribute.jurisdictionIdNumbers;
    this.isAgencyClient = attribute.isAgencyClient;
    this.quickbooksMapping = attribute.quickbooksMapping;
    this._isArchived = attribute.isArchived;
  }

  isAgency(): this is Agency {
    return !!this._isAgency;
  }

  isArchived() {
    return this._isArchived;
  }

  hasSamePhysicalAndMailingAddress() {
    return isEqual(this.address, this.mailingAddress);
  }
}

export class ClientList {
  constructor(public readonly clients: Client[]) {}

  agencies(): Agency[] {
    return this.clients.filter((client): client is Agency => client.isAgency());
  }
}

export interface ClientSummary {
  name: string;
  id: string;
  doingBusinessAs?: string;
  currency?: BillingCurrency;
  paymentMethod?: PaymentMethod;
  defaultBillToClientId?: string;
  invoicingInstructions?: string;
  isAgencyClient?: boolean;
  quickbooksCADId?: string;
  quickbooksUSDId?: string;
}

export class ClientSummaryList {
  constructor(public readonly clients: ClientSummary[]) {}

  toSelector(): ClientForSelector[] {
    return this.clients.map((client) => ({
      id: client.id,
      name: client.name,
      doingBusinessAs: client.doingBusinessAs,
    }));
  }

  find(id: string): ClientSummary | undefined {
    return this.clients.find((client) => client.id === id);
  }
}
